import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'

import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { updateTripChatItem } from 'tripPlanner/api'
import type {
  TransportOption,
  TripItineraryData,
  TripItineraryItem,
  TripItemWithoutDay,
  UpdateTripChatItemResponse,
} from 'tripPlanner/api/types'
import * as ChatKeys from 'tripPlanner/hooks/reactQueryKeys/chat'
import { normalizeTripListNote } from '../utils/tripListNoteUtils'
import { updateTripItineraryItem } from '../utils/tripListItineraryItemUpdateUtils'

interface UpdateTripListNoteParams {
  rowId: string
  itemId: number
  notes?: string
}

interface MutationContext {
  previousItinerary?: TripItineraryData
}

function updateItemNotes<Item extends TripItineraryItem | TripItemWithoutDay>(
  item: Item,
  itemId: number,
  notes: string | undefined,
): Item {
  if (item.id !== itemId || item.notes === notes) return item
  return { ...item, notes }
}

function updateTransportOptionNotes(
  option: TransportOption,
  itemId: number,
  notes: string | undefined,
): TransportOption {
  if (option.itemId !== itemId || option.notes === notes) return option
  return { ...option, notes }
}

export function updateTripItineraryItemNotes(
  itineraryData: TripItineraryData | undefined,
  itemId: number,
  notes: string | undefined,
): TripItineraryData | undefined {
  return updateTripItineraryItem(itineraryData, {
    updateItem: (item) => updateItemNotes(item, itemId, notes),
    updateTransportOption: (option) => updateTransportOptionNotes(option, itemId, notes),
  })
}

export function useTripListNoteUpdate(chatId: number | undefined, region: string | undefined) {
  const intl = useIntl()
  const queryClient = useQueryClient()
  const [pendingRowId, setPendingRowId] = useState<string | undefined>(undefined)
  const itineraryQueryKey = ChatKeys.itineraryDetail(chatId ?? 0, region)

  const { mutate, isPending: isUpdating } = useMutation<UpdateTripChatItemResponse, unknown, UpdateTripListNoteParams, MutationContext>({
    mutationFn: async(params) => updateTripChatItem(params.itemId, { notes: normalizeTripListNote(params.notes) ?? null }),
    onMutate: async(params) => {
      const notes = normalizeTripListNote(params.notes)
      setPendingRowId(params.rowId)
      await queryClient.cancelQueries({ queryKey: itineraryQueryKey })
      const previousItinerary = queryClient.getQueryData<TripItineraryData>(itineraryQueryKey)
      queryClient.setQueryData<TripItineraryData | undefined>(
        itineraryQueryKey,
        (previous) => updateTripItineraryItemNotes(previous, params.itemId, notes),
      )
      return { previousItinerary }
    },
    onError: (_error, _params, context) => {
      queryClient.setQueryData(itineraryQueryKey, context?.previousItinerary)
      showSnackbar(
        intl.formatMessage({
          id: '3Cz-caVK1AsMF_QAcyZJ',
          defaultMessage: 'Failed to update note. Please try again.',
          description: 'Snackbar error shown when saving an itinerary list note fails in TripChat',
        }),
        'critical',
      )
    },
    onSettled: async() => {
      setPendingRowId(undefined)
      await queryClient.invalidateQueries({ queryKey: itineraryQueryKey })
    },
  })

  const updateNote = useCallback((params: UpdateTripListNoteParams) => {
    if (!chatId) return
    mutate(params)
  }, [chatId, mutate])

  return {
    isUpdating,
    pendingRowId,
    updateNote,
  }
}
